import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedEditor,
  SerializedLexicalNode,
  Spread
} from 'lexical';

import { $applyNodeReplacement, createEditor, DecoratorNode } from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';

const AudioComponent = React.lazy(() => import('./AudioComponent'));

export interface AudioPayload {
  src: string;
  caption?: LexicalEditor;
  key?: NodeKey;
  type?: string;
  showCaption?: boolean;
  captionsEnabled?: boolean;
}

function $convertAudioElement(domNode: Node): null | DOMConversionOutput {
  const audio = domNode as HTMLAudioElement;
  if (audio.src.startsWith('file://')) {
    return null;
  }
  const { src } = audio;
  const node = $createAudioNode({ src, type: 'audio' });
  return { node };
}

export type SerializedAudioNode = Spread<
  {
    src: string;
    caption: SerializedEditor;
    showCaption: boolean;
  },
  SerializedLexicalNode
>;

export class AudioNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __showCaption: boolean;
  __caption: LexicalEditor;
  __width: number | 'inherit';
  __maxWidth: number;
  __captionsEnabled: boolean;

  static getType(): string {
    return 'audio';
  }

  static clone(node: AudioNode): AudioNode {
    return new AudioNode(
      node.__src,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedAudioNode): AudioNode {
    const { src, caption, showCaption, type } = serializedNode;
    const node = $createAudioNode({
      src,
      showCaption,
      type
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('audio');
    element.setAttribute('src', this.__src);
    element.setAttribute('controls', 'true'); // Enable audio controls by default
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      audio: (node: Node) => ({
        conversion: $convertAudioElement,
        priority: 0
      })
    };
  }

  constructor(
    src: string,
    showCaption?: boolean,
    caption?: LexicalEditor,
    captionsEnabled?: boolean,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__showCaption = showCaption || false;
    this.__caption =
      caption ||
      createEditor({
        nodes: []
      });
    this.__captionsEnabled = captionsEnabled || captionsEnabled === undefined;
  }

  exportJSON(): SerializedAudioNode {
    return {
      src: this.__src,
      caption: this.__caption.toJSON(),
      showCaption: this.__showCaption,
      type: 'audio',
      version: 1
    };
  }

  setWidth(width: 'inherit' | number): void {
    const writable = this.getWritable();
    writable.__width = width;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.media;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <AudioComponent
          src={this.__src}
          width={this.__width}
          maxWidth={this.__maxWidth}
          nodeKey={this.getKey()}
        />
      </Suspense>
    );
  }
}

export function $createAudioNode({
  src,
  showCaption,
  caption,
  captionsEnabled,
  key
}: AudioPayload): AudioNode {
  return $applyNodeReplacement(new AudioNode(src, showCaption, caption, captionsEnabled, key));
}

export function $isAudioNode(node: LexicalNode | null | undefined): node is AudioNode {
  return node instanceof AudioNode;
}
